// src/components/AIAgent/MetricsDashboard.jsx

import React, { useEffect, useState } from "react";
import { useAIAgentStore } from "../../services/useAIAgentStore";
import "./AIAgent.css";

const MetricsDashboard = () => {
  const {
    dailyMetrics,
    metricsTrends,
    tokenUsage,
    workspaceMetrics,
    loading,
    error,
    fetchDailyMetrics,
    fetchMetricsTrends,
    fetchTokenUsage,
    fetchWorkspaceMetrics,
    triggerMetricsAggregation,
    clearError,
  } = useAIAgentStore();

  const [selectedPeriod, setSelectedPeriod] = useState(30);
  const [aggregating, setAggregating] = useState(false);

  useEffect(() => {
    loadMetrics();
  }, [selectedPeriod]);

  const loadMetrics = async () => {
    await Promise.all([
      fetchDailyMetrics(selectedPeriod),
      fetchMetricsTrends(selectedPeriod),
      fetchTokenUsage(selectedPeriod),
      fetchWorkspaceMetrics(),
    ]);
  };

  const handleAggregate = async () => {
    setAggregating(true);
    try {
      await triggerMetricsAggregation(7);
      await loadMetrics();
    } catch (err) {
      console.error("Error aggregating:", err);
    } finally {
      setAggregating(false);
    }
  };

  const formatNumber = (num) => {
    if (!num) return "0";
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}k`;
    return num.toString();
  };

  const formatPercent = (num) => {
    if (num === null || num === undefined) return "-";
    return `${num >= 0 ? "+" : ""}${num.toFixed(1)}%`;
  };

  const getTrendClass = (value, inverse = false) => {
    if (value === null || value === undefined) return "";
    if (inverse) {
      return value < 0 ? "trend-positive" : value > 0 ? "trend-negative" : "";
    }
    return value > 0 ? "trend-positive" : value < 0 ? "trend-negative" : "";
  };

  // Simple bar chart component
  const SimpleBarChart = ({ data, dataKey, height = 120, color = "#3b82f6" }) => {
    if (!data || data.length === 0) return <div className="no-chart-data">Sem dados</div>;

    const values = data.map((d) => {
      let val = d;
      for (const key of dataKey.split(".")) {
        val = val?.[key];
      }
      return val || 0;
    });

    const maxValue = Math.max(...values, 1);
    const barWidth = Math.max(100 / data.length - 2, 2);

    return (
      <div className="simple-chart" style={{ height }}>
        <svg width="100%" height="100%" viewBox={`0 0 100 ${height}`} preserveAspectRatio="none">
          {values.map((value, index) => {
            const barHeight = (value / maxValue) * (height - 20);
            const x = (index / data.length) * 100 + 1;
            const y = height - barHeight - 10;

            return (
              <rect
                key={index}
                x={`${x}%`}
                y={y}
                width={`${barWidth}%`}
                height={barHeight}
                fill={color}
                rx="2"
                opacity="0.8"
              />
            );
          })}
        </svg>
      </div>
    );
  };

  // Simple line chart component
  const SimpleLineChart = ({ data, dataKey, height = 120, color = "#22c55e" }) => {
    if (!data || data.length === 0) return <div className="no-chart-data">Sem dados</div>;

    const values = data.map((d) => {
      let val = d;
      for (const key of dataKey.split(".")) {
        val = val?.[key];
      }
      return val || 0;
    });

    const maxValue = Math.max(...values, 1);
    const minValue = Math.min(...values, 0);
    const range = maxValue - minValue || 1;

    const points = values
      .map((value, index) => {
        const x = (index / (data.length - 1 || 1)) * 100;
        const y = height - 10 - ((value - minValue) / range) * (height - 20);
        return `${x},${y}`;
      })
      .join(" ");

    return (
      <div className="simple-chart" style={{ height }}>
        <svg width="100%" height="100%" viewBox={`0 0 100 ${height}`} preserveAspectRatio="none">
          <polyline
            points={points}
            fill="none"
            stroke={color}
            strokeWidth="2"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      </div>
    );
  };

  if (loading && !dailyMetrics.length) {
    return <div className="metrics-dashboard loading">Carregando metricas...</div>;
  }

  return (
    <div className="metrics-dashboard">
      {/* Header */}
      <div className="metrics-header">
        <h3>Dashboard de Metricas</h3>
        <div className="metrics-controls">
          <select value={selectedPeriod} onChange={(e) => setSelectedPeriod(Number(e.target.value))}>
            <option value={7}>Ultimos 7 dias</option>
            <option value={14}>Ultimos 14 dias</option>
            <option value={30}>Ultimos 30 dias</option>
            <option value={90}>Ultimos 90 dias</option>
          </select>
          <button className="btn-refresh" onClick={loadMetrics} disabled={loading}>
            Atualizar
          </button>
          <button className="btn-aggregate" onClick={handleAggregate} disabled={aggregating}>
            {aggregating ? "Agregando..." : "Agregar Metricas"}
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* KPI Cards with Trends */}
      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-header">
            <span className="kpi-label">Total de Mensagens</span>
            {metricsTrends?.trends?.messages && (
              <span className={`kpi-trend ${getTrendClass(metricsTrends.trends.messages.change_percent)}`}>
                {formatPercent(metricsTrends.trends.messages.change_percent)}
              </span>
            )}
          </div>
          <div className="kpi-value">{formatNumber(workspaceMetrics?.messages?.total)}</div>
          <SimpleBarChart data={dailyMetrics} dataKey="messages.total" height={60} color="#3b82f6" />
        </div>

        <div className="kpi-card">
          <div className="kpi-header">
            <span className="kpi-label">Tokens Consumidos</span>
            {metricsTrends?.trends?.tokens && (
              <span className={`kpi-trend ${getTrendClass(metricsTrends.trends.tokens.change_percent)}`}>
                {formatPercent(metricsTrends.trends.tokens.change_percent)}
              </span>
            )}
          </div>
          <div className="kpi-value">{formatNumber(tokenUsage?.usage?.total_tokens)}</div>
          <SimpleBarChart data={dailyMetrics} dataKey="tokens.total" height={60} color="#8b5cf6" />
        </div>

        <div className="kpi-card">
          <div className="kpi-header">
            <span className="kpi-label">Tempo de Resposta (ms)</span>
            {metricsTrends?.trends?.response_time && (
              <span
                className={`kpi-trend ${getTrendClass(metricsTrends.trends.response_time.change_percent, true)}`}
              >
                {formatPercent(metricsTrends.trends.response_time.change_percent)}
              </span>
            )}
          </div>
          <div className="kpi-value">{workspaceMetrics?.messages?.avg_latency_ms || "-"}ms</div>
          <SimpleLineChart data={dailyMetrics} dataKey="performance.avg_response_ms" height={60} color="#f59e0b" />
        </div>

        <div className="kpi-card">
          <div className="kpi-header">
            <span className="kpi-label">Taxa de Escalacao</span>
            {metricsTrends?.trends?.escalation_rate && (
              <span
                className={`kpi-trend ${getTrendClass(metricsTrends.trends.escalation_rate.change_percent, true)}`}
              >
                {formatPercent(metricsTrends.trends.escalation_rate.change_percent)}
              </span>
            )}
          </div>
          <div className="kpi-value">
            {workspaceMetrics?.conversations?.total > 0
              ? `${((workspaceMetrics.conversations.escalated / workspaceMetrics.conversations.total) * 100).toFixed(1)}%`
              : "0%"}
          </div>
          <SimpleLineChart data={dailyMetrics} dataKey="quality.escalation_rate" height={60} color="#ef4444" />
        </div>
      </div>

      {/* Token Usage Details */}
      {tokenUsage && (
        <div className="metrics-section">
          <h4>Uso de Tokens</h4>
          <div className="token-details-grid">
            <div className="token-detail-card">
              <span className="detail-label">Tokens de Prompt</span>
              <span className="detail-value">{formatNumber(tokenUsage.usage?.prompt_tokens)}</span>
            </div>
            <div className="token-detail-card">
              <span className="detail-label">Tokens de Resposta</span>
              <span className="detail-value">{formatNumber(tokenUsage.usage?.completion_tokens)}</span>
            </div>
            <div className="token-detail-card highlight">
              <span className="detail-label">Tokens Economizados</span>
              <span className="detail-value success">{formatNumber(tokenUsage.savings?.tokens_saved)}</span>
            </div>
            <div className="token-detail-card highlight">
              <span className="detail-label">Eficiencia</span>
              <span className="detail-value success">{tokenUsage.savings?.efficiency_percent || 0}%</span>
            </div>
          </div>

          {/* Cost Estimates */}
          <div className="cost-estimates">
            <div className="cost-item">
              <span>Custo Estimado:</span>
              <span className="cost-value">${tokenUsage.estimates?.estimated_cost?.toFixed(4) || "0.00"}</span>
            </div>
            <div className="cost-item">
              <span>Economia Estimada:</span>
              <span className="cost-value success">${tokenUsage.estimates?.estimated_savings?.toFixed(4) || "0.00"}</span>
            </div>
          </div>
        </div>
      )}

      {/* Conversation Stats */}
      <div className="metrics-section">
        <h4>Conversas</h4>
        <div className="conversation-stats-grid">
          <div className="conv-stat-card">
            <span className="stat-number">{workspaceMetrics?.conversations?.total || 0}</span>
            <span className="stat-label">Total</span>
          </div>
          <div className="conv-stat-card active">
            <span className="stat-number">{workspaceMetrics?.conversations?.active || 0}</span>
            <span className="stat-label">Ativas</span>
          </div>
          <div className="conv-stat-card escalated">
            <span className="stat-number">{workspaceMetrics?.conversations?.escalated || 0}</span>
            <span className="stat-label">Escaladas</span>
          </div>
          <div className="conv-stat-card completed">
            <span className="stat-number">{workspaceMetrics?.conversations?.completed || 0}</span>
            <span className="stat-label">Finalizadas</span>
          </div>
        </div>
      </div>

      {/* Daily Stats Table */}
      {dailyMetrics.length > 0 && (
        <div className="metrics-section">
          <h4>Historico Diario</h4>
          <div className="daily-stats-table-container">
            <table className="daily-stats-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Mensagens</th>
                  <th>Tokens</th>
                  <th>Tempo Resp.</th>
                  <th>Escalacoes</th>
                </tr>
              </thead>
              <tbody>
                {dailyMetrics.slice(-10).reverse().map((day) => (
                  <tr key={day.date}>
                    <td>{new Date(day.date).toLocaleDateString("pt-BR")}</td>
                    <td>{day.messages?.total || 0}</td>
                    <td>{formatNumber(day.tokens?.total)}</td>
                    <td>{day.performance?.avg_response_ms || "-"}ms</td>
                    <td>{day.conversations?.escalated || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default MetricsDashboard;
